'use client';

import { useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { usePaymentConfig } from '@/tenants/_shared/hooks/usePaymentConfig';
import { useDonationCurrency } from '@/tenants/_shared/hooks/useCurrencies';
import {
  type DonationStrategy,
  type DonationSessionInput,
  type ActivePaymentSession,
} from './types';
import { useSelfAttribution } from './useSelfAttribution';

export function useEventDonationStrategy(eventId: string | undefined): DonationStrategy {
  const {
    data: paymentConfig,
    isLoading: isPaymentConfigLoading,
    isFetching: isPaymentConfigFetching,
    isError: isPaymentConfigError,
  } = usePaymentConfig(eventId);

  const currency = useDonationCurrency(eventId);
  const selfAttribution = useSelfAttribution();

  const createEventSession = trpc.publicDonation.createEventSession.useMutation();

  return useMemo(
    (): DonationStrategy => ({
      paymentConfig,
      isPaymentConfigLoading,
      isPaymentConfigFetching,
      isPaymentConfigError,
      currency,
      isFundraisingActive: paymentConfig?.isFundraisingActive ?? true,
      isSessionCreationPending: createEventSession.isPending,
      selfAttribution,
      checkoutScope: { scope: 'event' as const, eventId: eventId! },
      createSession: (input: DonationSessionInput): Promise<ActivePaymentSession> =>
        createEventSession.mutateAsync({
          ...input,
          eventId: eventId!,
        }),
    }),
    [
      paymentConfig,
      isPaymentConfigLoading,
      isPaymentConfigFetching,
      isPaymentConfigError,
      currency,
      selfAttribution,
      createEventSession,
      eventId,
    ],
  );
}
